import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useState
} from 'react';
import {
    UIButton,
    UIFormInput,
    UIInlineAlert,
    UISpacer,
    UIText,
    UITextButton
} from '@/ui';
import { useForgotPasswordAction } from '../actions';
import styles from './forgot-password.module.scss';
import { Link } from 'react-router';
import { validateEmail } from '@/common/util/validators/email';
import { useMeTranslation } from '../hooks';

type ForgotPasswordProps = {
    defaultEmail?: string;
    onSuccess?: (data: { email: string }) => void;
};

export function ForgotPassword(props: ForgotPasswordProps): ReactElement {
    const { defaultEmail, onSuccess } = props;

    const { t } = useMeTranslation();

    const { forgotPassword, loading, error, clearError } =
        useForgotPasswordAction();

    return (
        <>
            <UIText variant="h2" bold>
                {t('shared.passwords.reset_title')}
            </UIText>
            <UISpacer multiplier={2} />
            <UIText>{t('components.forgot_password.description')}</UIText>
            <UISpacer multiplier={2} />
            {error && (
                <>
                    <UIInlineAlert text={error} onClose={() => clearError()} />
                    <UISpacer multiplier={2} />
                </>
            )}
            <ForgotPasswordForm
                loading={loading}
                defaultValues={{ email: defaultEmail }}
                onSubmit={(data) => {
                    forgotPassword({ email: data.email })
                        .then(() => {
                            onSuccess?.(data);
                        })
                        .catch(() => {}); // Handled in the hook
                }}
            />
        </>
    );
}

type ForgotPasswordFormProps = {
    onSubmit?: (data: { email: string }) => void;
    loading?: boolean;
    defaultValues?: { email?: string };
};
export function ForgotPasswordForm(
    props: ForgotPasswordFormProps
): ReactElement {
    const { t } = useMeTranslation();

    const { onSubmit, loading, defaultValues } = props;

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Email
     */
    const [email, setEmail] = useState(defaultValues?.email || '');
    const [emailError, setEmailError] = useState('');
    const checkEmail = useCallback(
        (email: string): string => {
            const error = validateEmail(email);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setEmailError(checkEmail(email));
        }
    }, [email, showRealTimeErrors, checkEmail]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkEmail(email)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ email });
        }
    };

    return (
        <div className={styles.container}>
            <form onSubmit={handleSubmit} noValidate>
                <UIFormInput
                    name="email"
                    label={t('shared.fields.email.label')}
                    value={email}
                    type="email"
                    errorText={emailError}
                    required
                    maxLength={320}
                    onChange={setEmail}
                />
                <UISpacer multiplier={2} />
                <div className={styles.buttonContainer}>
                    <UIButton
                        variant="primary"
                        type="submit"
                        loading={loading}
                        disabled={loading}
                    >
                        {t('shared.passwords.reset_submit')}
                    </UIButton>
                    <UISpacer multiplier={1} />
                    <Link to={'/login'} style={{ textDecoration: 'none' }}>
                        <UITextButton variant="primary">
                            {t('shared.labels.return_to_login')}
                        </UITextButton>
                    </Link>
                </div>
            </form>
        </div>
    );
}

type ForgotPasswordSuccessProps = {
    onResend?: () => void;
};

export function ForgotPasswordSuccess(
    props: ForgotPasswordSuccessProps
): ReactElement {
    const { onResend } = props;

    const { t } = useMeTranslation();

    return (
        <>
            <UIText variant="h2" bold>
                {t('components.forgot_password.success_title')}
            </UIText>
            <UISpacer multiplier={2} />
            <UIText>
                {t('components.forgot_password.success_description')}
            </UIText>
            <UISpacer multiplier={2} />
            <UITextButton variant="primary" onClick={() => onResend?.()}>
                {t('components.forgot_password.resend')}
            </UITextButton>
        </>
    );
}
